import { HTTPMethod } from '../../types/enums';
import { BaseAdapterInterface } from '../../types/global';
import getBackendUrl from '../../utils/backendUrl';

const expiryCookieName = 'token_expiry';
// Refresh if the token is due to expire within the next 5 minutes
const nearExpiryWindowMs = 5 * 60 * 1000;
// Refresh if the token expired within the last 5 minutes
const recentlyExpiredWindowMs = 5 * 60 * 1000;

// Shared across all BaseAdapter instances — ensures only one refresh request is in-flight at a time
let refreshTokenPromise: Promise<void> | null = null;

// Shared across all BaseAdapter instances — ensures only one session-expired redirect is queued at a time
let sessionExpiredRedirectPromise: Promise<void> | null = null;
export function isSessionExpiredRedirectQueued() { return sessionExpiredRedirectPromise !== null; }
export function resetSessionExpiredRedirect() { sessionExpiredRedirectPromise = null; }

function getCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((r) => r.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

export default class BaseAdapter<BackendType> implements BaseAdapterInterface {
  rawResponse: BackendType;

  private static getBaseUrl(): string {
    return getBackendUrl();
  }

  // eslint-disable-next-line class-methods-use-this
  protected navigateTo(url: string) {
    window.location.href = url;
  }

  async fetchApiResponse(endpoint: string, method: HTTPMethod, data?: any, signal?: AbortSignal) {
    const tokenExpiry = getCookie(expiryCookieName);
    if (tokenExpiry) {
      // tokenExpiry is expected to be a timestamp in milliseconds
      const tokenTimeToExpiry = Number(tokenExpiry) - Date.now();
      const isExpiringSoon = tokenTimeToExpiry >= 0 && tokenTimeToExpiry < nearExpiryWindowMs;
      const isRecentlyExpired = tokenTimeToExpiry < 0 && -tokenTimeToExpiry < recentlyExpiredWindowMs;
      const isDefinitivelyExpired = tokenTimeToExpiry < 0 && -tokenTimeToExpiry >= recentlyExpiredWindowMs;
      if (isDefinitivelyExpired) {
        if (!sessionExpiredRedirectPromise) {
          document.cookie = 'token_expiry=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
          sessionExpiredRedirectPromise = new Promise<void>((resolve) => {
            this.navigateTo('/login?reason=session_expired');
            resolve();
          });
        }
        await sessionExpiredRedirectPromise;
        return;
      }
      if (isExpiringSoon || isRecentlyExpired) {
        if (!refreshTokenPromise) {
          refreshTokenPromise = fetch(`${BaseAdapter.getBaseUrl()}/backend/auth/refresh`, { credentials: 'include' })
            .then(() => {})
            .finally(() => { refreshTokenPromise = null; });
        }
        await refreshTokenPromise;
      }
    }

    const requestOptions: RequestInit = {
      method,
      credentials: 'include',
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': 'application/json',
      },
    };
    if (signal) {
      requestOptions.signal = signal;
    }
    if (data) {
      requestOptions.body = JSON.stringify(data);
    }
    const url = `${BaseAdapter.getBaseUrl()}${endpoint}`;
    const response = await fetch(url, requestOptions);
    if (response.status === 429) {
      throw new Error('Too many requests. Please try again shortly.');
    }
    if (!response.ok) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const errorResponse: { detail: string } = await response.json();
      throw new Error(errorResponse.detail);
    }
    const text = await response.text();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.rawResponse = text ? JSON.parse(text) : undefined;
  }
}
